import { consoleLogger } from '@shared/logging/consoleLogger';
import {
  addEventListener,
  removeEventListener,
} from '@shared/api/sharedAPIEventFunctions';
import { getLoggingLevelCookieName } from '@shared/logging/getLoggingLevelCookieName';
import { LogLevelType } from '@shared/content/types';
import { getValidatedBase64Cookie } from '@shared/cookies/getValidatedBase64Cookie';
import { createCookie } from '@shared/cookies/createCookie';
import { encodeToBase64 } from '@shared/conversion/encodeToBase64';

export const initializeSharedApiFunctions = (environment: string) => {
  const loggingLevelCookieName = getLoggingLevelCookieName();

  /**
   * API method to set verbose logging
   * @param level
   */
  const setLogLevel = (level: LogLevelType) => {
    const loggingLevelCookieValue = getValidatedBase64Cookie(
      document.cookie,
      loggingLevelCookieName,
    );
    const isLoggingLevelAlreadySet = loggingLevelCookieValue
      ? loggingLevelCookieValue === level
      : false;

    if (isLoggingLevelAlreadySet) {
      // eslint-disable-next-line no-console
      console.info(
        consoleLogger.prefix,
        `Logging level (${level}) is already set`,
      );
    } else if (level in consoleLogger.logLevels) {
      document.cookie = createCookie({
        name: loggingLevelCookieName,
        value: encodeToBase64(level),
        days: 90,
      });
      consoleLogger.setLogLevel(level);
      // eslint-disable-next-line no-console
      console.info(
        consoleLogger.prefix,
        `Logging level (${level}) has been set`,
      );
    } else {
      // eslint-disable-next-line no-console
      console.info(
        consoleLogger.prefix,
        `Logging level (${level}) does not exist. Allowed logging levels:`,
        consoleLogger.logLevels,
      );
    }
  };

  /* Legacy API method to set verbose logging */
  const setVerboseLoggingLevel = () => {
    const loggingLevelCookieValue = getValidatedBase64Cookie(
      document.cookie,
      loggingLevelCookieName,
    );
    const isLoggingLevelAlreadyVerbose = loggingLevelCookieValue
      ? loggingLevelCookieValue === 'verbose'
      : false;
    if (!isLoggingLevelAlreadyVerbose) {
      document.cookie = createCookie({
        name: loggingLevelCookieName,
        value: encodeToBase64('verbose'),
        days: 90,
      });
    }
    consoleLogger.logInfo('Verbose logging level has been set');
    consoleLogger.setLogLevel('verbose');
  };

  /* API method to reset logging level back to info */
  const resetLoggingLevel = () => {
    if (window.Utiq?.config?.logLevel) {
      window.Utiq.API?.setLogLevel!(window.Utiq.config.logLevel);
    } else {
      document.cookie = createCookie({
        name: loggingLevelCookieName,
        value: encodeToBase64(),
        days: 0,
      });
      consoleLogger.logInfo(
        `Logging level has been reset to ${consoleLogger.defaultLogLevel} level`,
      );
      consoleLogger.setLogLevel(consoleLogger.defaultLogLevel);
    }
  };

  window.Utiq ||= {};
  window.Utiq.API ||= {};
  window.Utiq.API.setLogLevel = setLogLevel;
  window.Utiq.API.setVerboseLoggingLevel = setVerboseLoggingLevel;
  window.Utiq.API.resetLoggingLevel = resetLoggingLevel;
  window.Utiq.API.addEventListener =
    addEventListener as typeof window.Utiq.API.addEventListener;
  window.Utiq.API.removeEventListener =
    removeEventListener as typeof window.Utiq.API.removeEventListener;

  consoleLogger.logDebug(`${environment} logging API functions initialized...`);
};
